import React, { useEffect, useState } from "react"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState, setTradeData, addNotification } from "@/store/globalSlice"
import { getUserCreds } from "@/utils/utils"
import { tradeOriginBadge } from "@/utils/tradeHelpers"
import { allPieces, images } from "@/utils/data"
import { tradeOfferTime } from "@mono/constants"

const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`

// How long an offer has left before it expires, on its own clock.
//
// The panel used to hold this: one `now` state ticking every second, which
// re-rendered the whole list — every row, both avatars, all the history below
// it — even with no offer on the table. The countdown is the only thing on the
// row that changes between server updates, so it is the only thing that ticks,
// and only while a row that shows one is mounted.
const TradeCountdownChip: React.FC<{ offeredAt?: number }> = ({ offeredAt }) => {
  const secondsLeft = () =>
    offeredAt
      ? Math.max(0, Math.ceil((offeredAt + tradeOfferTime * 1000 - Date.now()) / 1000))
      : tradeOfferTime
  const [remaining, setRemaining] = useState(secondsLeft)

  useEffect(() => {
    setRemaining(secondsLeft())
    if (!offeredAt) return
    const interval = setInterval(() => setRemaining(secondsLeft()), 1000)
    return () => clearInterval(interval)
  }, [offeredAt])

  return (
    <span
      className={classNames(
        "flex items-center gap-1 shrink-0 font-poppins font-bold tabular-nums text-sm rounded-md px-1.5 py-0.5",
        remaining <= 10 ? "text-red-300 bg-red-500/20" : "text-amber-200 bg-black/30"
      )}
    >
      <i className="fa-solid fa-clock text-xs"></i>
      {formatTime(remaining)}
    </span>
  )
}

// Caption + rule marking off one half of the list. The two halves look alike at
// a glance — same avatars, same arrow — so once both are on screen each needs a
// word saying whether its rows are live offers or ones that already ended.
const ListSectionLabel: React.FC<{ text: string; tone: string; className?: string }> = ({
  text,
  tone,
  className,
}) => (
  <div className={classNames("flex items-center gap-2 shrink-0 mx-1", className)}>
    <span
      className={classNames(
        "font-poppins font-bold text-[9px] uppercase tracking-widest whitespace-nowrap",
        tone
      )}
    >
      {text}
    </span>
    <div className="h-px flex-1 bg-white/10"></div>
  </div>
)

// Piece token for one side of a row. The circle itself still changes size to
// mark out an incoming offer, but the slot around it never does: every row —
// live, completed or rejected — gives each token the same 1.75rem of width so a
// bigger avatar can't push that row's second player out of line with its
// neighbours.
const PartyAvatarSlot: React.FC<{
  imgSrc?: string
  circleSize: string
  imgSize: string
  alert?: boolean
}> = ({ imgSrc, circleSize, imgSize, alert }) => (
  // justify-start, not center: the slot is sized for the largest avatar, and any
  // slack it has for a smaller one belongs on the inside of the row, not as dead
  // space against the row's left edge.
  <div className="relative flex items-center justify-start shrink-0 w-7">
    <div
      className={classNames(
        "flex items-center justify-center rounded-full bg-black/40 ring-1 ring-white/15",
        circleSize
      )}
    >
      {imgSrc && <img className={classNames("object-contain", imgSize)} src={imgSrc} />}
    </div>
    {/* "Needs you" marker rides on the avatar rather than sitting in its own
        column: a column would indent every row in the list by its width just to
        leave room for a dot only incoming rows ever draw. */}
    {alert && (
      <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-300 ring-1 ring-black/50 trade-dot-throb"></span>
    )}
  </div>
)

const ActiveTradesListPanel: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const situationPlayers = useAppSelector((state) => state.global.myGame?.situation?.players)
  const activeTrades = useAppSelector((state) => state.global.myGame?.situation?.activeTrades)
  const completedTrades = useAppSelector((state) => state.global.myGame?.situation?.completedTrades)
  const rejectedTrades = useAppSelector((state) => state.global.myGame?.situation?.rejectedTrades)

  // Rejected offers share the history list with completed trades; this filter
  // takes them back out for players who only care what actually went through.
  const [hideRejected, setHideRejected] = useState(false)

  const myOrder = situationPlayers?.find((p) => p.id === getUserCreds().email)?.order
  // Trades offered to me float to the top so they're easy to spot/act on.
  const sortedTrades = [...(activeTrades || [])].sort(
    (a, b) =>
      (b.offeredPlayerOrder === myOrder ? 1 : 0) - (a.offeredPlayerOrder === myOrder ? 1 : 0)
  )

  const openTrade = (trade: ITrade) => {
    dispatch(setTradeData({ activeTrade: { ...trade }, status: "offered" }))
    dispatch(setState({ viewedTradeId: trade.id }))
  }

  // Open the full trade-detail modal (the center "Successful trade" view) for a
  // completed trade. Stays until dismissed via its close button.
  const openCompletedTrade = (trade: ITrade) => {
    dispatch(
      addNotification({
        removeAll: true,
        type: "trade",
        eventType: "accepted",
        duration: 600000,
        ...trade,
      })
    )
  }

  // Same modal for a rejected offer — the trade's own rejectedAt is what puts it
  // in the rejection theme rather than reading as a live decline.
  const openRejectedTrade = (trade: ITrade) => {
    dispatch(
      addNotification({
        removeAll: true,
        type: "trade",
        eventType: "declined",
        duration: 600000,
        ...trade,
      })
    )
  }

  // Every row, live or historical, is built around the same "who offered whom".
  const getTradeParties = (trade: ITrade) => {
    const offering = situationPlayers?.find((p) => p.order === trade.offeringPlayerOrder)
    const offered = situationPlayers?.find((p) => p.order === trade.offeredPlayerOrder)
    const nameOf = (player?: IPlayer) =>
      player?.order === myOrder
        ? "You"
        : myGame?.players.find((u) => u.email === player?.id)?.username || "Player"
    return {
      offeringPiece: allPieces.find((pc) => pc.id === offering?.pieceId),
      offeredPiece: allPieces.find((pc) => pc.id === offered?.pieceId),
      offeringName: nameOf(offering),
      offeredName: nameOf(offered),
    }
  }

  // Trades waiting on the current player's response — these drive the prominent
  // "needs your response" call-to-action and the panel glow.
  const incomingTrades = sortedTrades.filter((t) => t.offeredPlayerOrder === myOrder)
  const hasIncoming = incomingTrades.length > 0

  // One history below the active trades: settled trades and turned-down offers
  // interleaved in the order they happened, newest first, each row carrying its
  // own verdict badge.
  const tradeHistory = [
    ...(completedTrades || []).map((trade) => ({
      trade,
      kind: "completed" as const,
      at: trade.completedAt || 0,
    })),
    ...(rejectedTrades || []).map((trade) => ({
      trade,
      kind: "rejected" as const,
      at: trade.rejectedAt || 0,
    })),
  ].sort((a, b) => b.at - a.at)
  const rejectedCount = rejectedTrades?.length || 0
  const visibleHistory = hideRejected
    ? tradeHistory.filter((h) => h.kind === "completed")
    : tradeHistory

  return (
    <div
      id="active-trades-panel"
      className={classNames(
        // Fills the sidebar's fixed-height bottom slot; shrinks when the "last
        // auction results" button shares that slot.
        "relative ml-4 flex flex-col flex-1 min-h-0 rounded-2xl border shadow-[0_10px_30px_rgba(0,0,0,0.5)] p-2.5",
        hasIncoming ? "trade-panel-alert border-amber-400/70" : "border-amber-400/20"
      )}
      style={{
        backgroundImage: `url(${images.playerItemBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Wraps rather than crushes: the chip group carries a word now, and on a
          narrow sidebar (its width is (100vw-100vh)/2, so tall-ish screens make
          it slim) the title + "for you" badge + chips can outrun one line. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5 px-1 shrink-0">
        <i className="fa-solid fa-right-left text-amber-400 text-sm"></i>
        <span className="shrink-0 text-white font-poppins font-semibold text-base tracking-wide">
          Trades
        </span>
        {hasIncoming && (
          <span className="flex items-center gap-1 text-[10px] font-poppins font-bold text-black bg-amber-400 rounded-full px-1.5 py-0.5 animate-pulse">
            <i className="fa-solid fa-bell text-[9px]"></i>
            {incomingTrades.length} for you
          </span>
        )}
        {/* Right-hand status chips, same pill shape so they read as one group:
            the rejection filter (only once there is something to hide) and the
            active-trade count. */}
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          {rejectedCount > 0 && (
            // Lit red while it is actually hiding something, so the shorter
            // list below is never a mystery; muted otherwise, where the count
            // is just "this many of the rows below are rejections".
            <button
              data-testid="trade.rejected-filter-btn"
              onClick={() => setHideRejected((v) => !v)}
              title={
                hideRejected
                  ? `${rejectedCount} rejected offer${rejectedCount === 1 ? "" : "s"} hidden — tap to show`
                  : `Hide ${rejectedCount} rejected offer${rejectedCount === 1 ? "" : "s"}`
              }
              className={classNames(
                "flex items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-0.5 cursor-pointer transition-colors",
                "font-poppins font-bold text-[10px]",
                hideRejected
                  ? "bg-red-500/25 border-red-400/60 text-red-200 hover:bg-red-500/40"
                  : "bg-white/[0.06] border-white/20 text-white/60 hover:border-white/40 hover:text-white"
              )}
            >
              <i
                className={classNames(
                  "text-[9px]",
                  hideRejected ? "fa-solid fa-eye-slash" : "fa-solid fa-ban"
                )}
              ></i>
              {hideRejected ? "Show" : "Hide"} rejected
              <span
                className={classNames(
                  "rounded-full px-1 tabular-nums",
                  hideRejected ? "bg-red-500/30 text-red-100" : "bg-white/10 text-white/70"
                )}
              >
                {rejectedCount}
              </span>
            </button>
          )}
          <span className="text-[10px] font-poppins font-bold text-amber-200 bg-amber-400/10 border border-amber-400/30 rounded-full px-1.5 py-0.5">
            {activeTrades?.length || 0}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1 overflow-y-auto pr-1 flex-1 min-h-0">
        {/* Only worth labelling the live rows once there is a settled half
            below them to tell them apart from. */}
        {visibleHistory.length > 0 && (
          <ListSectionLabel text="Active offers" tone="text-amber-200/70" className="mb-0.5" />
        )}
        {(activeTrades?.length || 0) === 0 ? (
          <div
            className={classNames(
              "flex items-center justify-center text-center text-white/60 font-poppins text-sm px-2",
              visibleHistory.length > 0 ? "py-3" : "flex-1"
            )}
          >
            No active trades
          </div>
        ) : (
          sortedTrades.map((trade) => {
            const { offeringPiece, offeredPiece, offeringName, offeredName } =
              getTradeParties(trade)
            const isOfferedToMe = trade.offeredPlayerOrder === myOrder
            const offeringIsMe = trade.offeringPlayerOrder === myOrder
            const avatarSize = isOfferedToMe ? "h-7 w-7" : "h-6 w-6"
            const imgSize = isOfferedToMe ? "h-5 w-5" : "h-4 w-4"
            const nameSize = isOfferedToMe ? "text-xs" : "text-[11px]"
            // The current player's "You" label is rendered larger and amber so it
            // stands out in the row.
            // The offering name is a fixed share of the row rather than flex-1:
            // it's what sets where the arrow, the second avatar and the offered
            // name begin, so it must not stretch to absorb the trailing chips —
            // those differ row to row (only incoming rows carry an origin badge)
            // and would slide the second player sideways. A third of the row
            // puts that second player at about its midpoint at any sidebar width.
            const nameClass = (isMe: boolean, isOffering: boolean) =>
              classNames(
                "min-w-0 text-left font-poppins truncate",
                isOffering ? "w-1/3 shrink-0" : "flex-1",
                isMe
                  ? "font-extrabold text-amber-300 text-base tracking-wide"
                  : classNames("font-medium text-white", nameSize)
              )
            // Same rule as the trade drawer's header badge: only the player the
            // offer is aimed at is told it's an update or a counter rather than
            // a first offer. Rows for other people's trades stay as they were.
            const originBadge = isOfferedToMe && trade.origin ? tradeOriginBadge(trade.origin) : null
            return (
              <button
                key={trade.id}
                onClick={() => openTrade(trade)}
                data-testid={`trade.list-item-${trade.id}`}
                title={isOfferedToMe ? "Offered to you — tap to review" : undefined}
                className={classNames(
                  "group relative overflow-hidden flex items-center gap-1 w-full shrink-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]",
                  {
                    // Same px as every other row in the list: its border is a
                    // pixel thicker, so matching padding classes would inset its
                    // contents further and break the shared column alignment.
                    "trade-incoming-row px-1.5 py-1.5 bg-gradient-to-br from-amber-400/30 to-amber-500/[0.08] border-2 border-amber-400":
                      isOfferedToMe,
                    "trade-item-enter px-1.5 py-1 bg-gradient-to-br from-white/[0.1] to-white/[0.03] border border-white/10 shadow-sm hover:border-amber-400/70 hover:from-amber-400/20 hover:to-amber-400/5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.45)]":
                      !isOfferedToMe,
                  }
                )}
              >
                {/* Sheen sweep + the avatar's pulsing dot signal this row needs a tap. */}
                {isOfferedToMe && <span className="trade-row-sheen pointer-events-none absolute inset-0"></span>}
                <PartyAvatarSlot
                  imgSrc={offeringPiece?.imgSrc}
                  circleSize={avatarSize}
                  imgSize={imgSize}
                  alert={isOfferedToMe}
                />
                <span className={nameClass(offeringIsMe, true)}>{offeringName}</span>
                {/* Fixed-width slot: the glyph is wider on the larger text size,
                    which would otherwise offset the column that follows it. */}
                <i className="fa-solid fa-arrow-right-long w-4 text-center text-amber-300 text-xs shrink-0 group-hover:text-amber-200 transition-colors"></i>
                <PartyAvatarSlot
                  imgSrc={offeredPiece?.imgSrc}
                  circleSize={avatarSize}
                  imgSize={imgSize}
                />
                <span className={nameClass(isOfferedToMe, false)}>{offeredName}</span>
                {originBadge && (
                  // Sits with the countdown at the end of the row: both are
                  // status on the offer rather than part of who-trades-with-whom.
                  // Icon-and-label at [9px] to match the other chips in this
                  // panel — the names beside it already truncate.
                  <span
                    data-testid={`trade.list-item-origin-${trade.id}`}
                    className={classNames(
                      "flex items-center gap-1 shrink-0 rounded-full border px-1.5 py-0.5",
                      "font-poppins font-semibold text-[9px] uppercase tracking-wide",
                      originBadge.className
                    )}
                  >
                    <i className={classNames(originBadge.icon, "text-[8px]")}></i>
                    {originBadge.label}
                  </span>
                )}
                <TradeCountdownChip offeredAt={trade.offeredAt} />
              </button>
            )
          })
        )}

        {visibleHistory.length > 0 && (
          <>
            {/* Everything below has already been settled — each row still
                carries its own Done/Rejected verdict, this just says up front
                that none of them are waiting on anyone. */}
            <ListSectionLabel
              text={hideRejected ? "Completed" : "Completed & rejected"}
              tone="text-white/40"
              className="mt-1.5 mb-0.5"
            />

            {visibleHistory.map(({ trade, kind }) => {
              const { offeringPiece, offeredPiece, offeringName, offeredName } =
                getTradeParties(trade)
              const isRejected = kind === "rejected"
              return (
                <button
                  key={`${kind}-${trade.id || `${trade.offeringPlayerOrder}-${trade.offeredPlayerOrder}-${trade.completedAt || trade.rejectedAt}`}`}
                  // Landing anchors for the minimize flight of a reopened
                  // trade: rows render newest-first, so the first row of a
                  // given kind in the document is that kind's latest trade.
                  id={trade.id ? `${kind}-trade-row-${trade.id}` : undefined}
                  data-completed-trade-row={isRejected ? undefined : true}
                  data-rejected-trade-row={isRejected ? true : undefined}
                  data-testid={
                    isRejected ? `trade.rejected-list-item-${trade.id}` : undefined
                  }
                  onClick={() =>
                    isRejected ? openRejectedTrade(trade) : openCompletedTrade(trade)
                  }
                  title={
                    isRejected
                      ? `${offeredName} rejected this offer — tap to view details`
                      : "View trade details"
                  }
                  className={classNames(
                    "flex items-center gap-1 w-full shrink-0 rounded-md px-1.5 py-0.5 border cursor-pointer transition-colors",
                    isRejected
                      ? "bg-red-500/10 border-red-400/30 hover:bg-red-500/20 hover:border-red-400/70"
                      : "bg-white/[0.05] border-white/10 hover:bg-white/[0.1] hover:border-emerald-400/50"
                  )}
                >
                  <PartyAvatarSlot
                    imgSrc={offeringPiece?.imgSrc}
                    circleSize="h-5 w-5"
                    imgSize="h-3.5 w-3.5"
                  />
                  {/* Fixed width, not flex-1: the second avatar's x-position must stay
                      constant across rows regardless of the trailing badge's width
                      ("Rejected" vs "Done"), so this column can't flex to absorb it. */}
                  <span className="min-w-0 w-1/3 shrink-0 text-left font-poppins font-medium text-white text-[10px] truncate">
                    {offeringName}
                  </span>
                  <i
                    className={classNames(
                      "fa-solid fa-arrow-right-long w-4 text-center text-[9px] shrink-0",
                      isRejected ? "text-red-300" : "text-amber-300"
                    )}
                  ></i>
                  <PartyAvatarSlot
                    imgSrc={offeredPiece?.imgSrc}
                    circleSize="h-5 w-5"
                    imgSize="h-3.5 w-3.5"
                  />
                  <span className="min-w-0 flex-1 text-left font-poppins font-medium text-white text-[10px] truncate">
                    {offeredName}
                  </span>
                  {/* How it ended. The two kinds sit interleaved in one list,
                      so every row has to carry its own verdict. */}
                  <span
                    className={classNames(
                      "flex items-center gap-1 shrink-0 rounded-full border px-1.5 py-0.5",
                      "font-poppins font-bold text-[9px] uppercase tracking-wide",
                      isRejected
                        ? "bg-red-500/30 border-red-400/60 text-red-200"
                        : "bg-emerald-500/30 border-emerald-400/60 text-emerald-200"
                    )}
                  >
                    <i
                      className={classNames(
                        "text-[8px]",
                        isRejected ? "fa-solid fa-ban" : "fa-solid fa-check"
                      )}
                    ></i>
                    {isRejected ? "Rejected" : "Done"}
                  </span>
                </button>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}

export default React.memo(ActiveTradesListPanel)
